import { useStore } from './store';

type ConfirmationStatus = 'confirmed' | 'cancelled';

/**
 * Gets the status of a specific confirmation nonce from the Zustand store.
 */
export const getConfirmationStatus = (nonce: string): ConfirmationStatus | null => {
  return useStore.getState().confirmations[nonce]?.status || null;
};

/**
 * Sets the status of a specific confirmation nonce in the Zustand store.
 */
export const setConfirmationStatus = (
  nonce: string, 
  status: ConfirmationStatus, 
  threadId?: string
): void => {
  useStore.getState().setConfirmation(nonce, status, threadId);
};

/**
 * Removes all confirmation records associated with a specific thread ID from the Zustand store.
 */
export const cleanupConfirmationsByThread = (threadId: string): void => {
  useStore.getState().clearConfirmations(threadId);
};

/**
 * Removes all confirmation records from the Zustand store.
 */
export const clearAllConfirmations = (): void => {
  useStore.getState().clearConfirmations();
};

/**
 * Legacy migration: Move individual confirm_status_* keys into the consolidated Zustand store.
 */
export const migrateLegacyConfirmations = (): void => {
  const state = useStore.getState();
  let hasChanges = false;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('confirm_status_')) {
      const nonce = key.replace('confirm_status_', '');
      const status = localStorage.getItem(key);
      
      if (status === 'confirmed' || status === 'cancelled') {
        if (!state.confirmations[nonce]) {
          state.setConfirmation(nonce, status as ConfirmationStatus);
          hasChanges = true;
        }
      }
    }
  }
  
  // Also migrate the old 'cartlens_confirmations' object if it exists
  const oldConsolidated = localStorage.getItem('cartlens_confirmations');
  if (oldConsolidated) {
    try {
      const parsed = JSON.parse(oldConsolidated);
      Object.keys(parsed).forEach(nonce => {
        if (!state.confirmations[nonce]) {
          const data = parsed[nonce];
          state.setConfirmation(nonce, data.status, data.threadId);
          hasChanges = true;
        }
      });
      localStorage.removeItem('cartlens_confirmations');
    } catch (e) {
      console.warn("Failed to migrate consolidated confirmations", e);
    }
  }
  
  if (hasChanges) {
    // Cleanup old individual keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('confirm_status_')) {
        localStorage.removeItem(key);
        i--;
      }
    }
  }
};
